import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";

// Reusable rich-text editor op TipTap. Output is HTML (sanitized server-side).
// Toolbar bevat de essenties voor admin-mails: bold, italic, lijst, link.
// Niet meer dan dat - we willen voorkomen dat admins kleurtjes en lettertypes
// in de mail proppen die in Gmail/Outlook toch niet renderen.

const ToolbarButton = ({ active, disabled, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={!!active}
    className={`inline-flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
      active
        ? "bg-brand-subtle text-brand"
        : "text-ink-subtle hover:bg-[#091E420F] hover:text-ink"
    }`}
  >
    {children}
  </button>
);

const Divider = () => <div className="mx-1 h-5 w-px bg-line" />;

const RichTextEditor = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  onReady,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? "",
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
      }),
    ],
    content: value ?? "",
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          "prose-sm max-w-none min-h-[180px] focus:outline-none px-3 py-2.5 text-sm text-ink [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-2 [&_ol]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1 [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-[15px] [&_h3]:font-bold [&_h3]:text-ink [&_h3:first-child]:mt-0 [&_strong]:font-bold [&_strong]:text-ink [&_a]:text-brand [&_a]:underline",
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap geeft "<p></p>" als de inhoud leeg is - normaliseer naar
      // empty string zodat de modal weet dat er geen tekst is.
      const isEmpty = ed.isEmpty;
      onChange?.(isEmpty ? "" : html);
    },
    immediatelyRender: false,
  });

  // Geeft de editor-instantie aan de parent, bv. om tekst op de cursor in
  // te voegen (variabelen in de mail-modal).
  useEffect(() => {
    if (editor) onReady?.(editor);
  }, [editor, onReady]);

  // Externe value-sync. Alleen overschrijven als de waarde echt anders is,
  // anders verliezen we de cursor-positie van de gebruiker.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value ?? "";
    if (current === next) return;
    if (next === "" && editor.isEmpty) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return (
      <div className="min-h-[220px] rounded border-2 border-line bg-sunken" />
    );
  }

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL:", prev ?? "https://");
    if (url === null) return; // cancel
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    let safe = url.trim();
    if (!/^https?:\/\//i.test(safe) && !safe.startsWith("mailto:")) {
      safe = `https://${safe}`;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
  };

  return (
    <div
      className={`overflow-hidden rounded border-2 border-line bg-white focus-within:border-brand ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line bg-sunken px-1.5 py-1">
        <ToolbarButton
          title="Vet (Cmd+B)"
          active={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="Cursief (Cmd+I)"
          active={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title="Doorhalen"
          active={editor.isActive("strike")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <span className="line-through">S</span>
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          title="Bullet-lijst"
          active={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •
        </ToolbarButton>
        <ToolbarButton
          title="Genummerde lijst"
          active={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1.
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          title="Kop (subkop)"
          active={editor.isActive("heading", { level: 3 })}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          <span className="text-xs font-semibold">H</span>
        </ToolbarButton>
        <ToolbarButton
          title="Citaat"
          active={editor.isActive("blockquote")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          ❝
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          title="Link"
          active={editor.isActive("link")}
          disabled={disabled}
          onClick={setLink}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
              clipRule="evenodd"
            />
          </svg>
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          title="Ongedaan maken"
          disabled={disabled || !editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          ↶
        </ToolbarButton>
        <ToolbarButton
          title="Opnieuw"
          disabled={disabled || !editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          ↷
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #626F86;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
